import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Card,
  CardContent,
  TextField,
  MenuItem,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';

import { useQuery } from '@tanstack/react-query';

interface LotteryResult {
  id: number;
  drawId: number;
  operatorId: number;
  operatorName: string;
  operatorCode: string;
  gameType: string;
  drawDate: string;
  firstPrize: string;
  secondPrize: string;
  thirdPrize: string;
  specialPrizes: string[];
  consolationPrizes: string[];
  createdAt: string;
}

interface OperatorLotteryResults {
  operatorId: number;
  operatorName: string;
  operatorCode: string;
  drawDate: string;
  results: GameTypeResult[];
}

interface GameTypeResult {
  gameType: string;
  firstPrize: string;
  secondPrize: string;
  thirdPrize: string;
  specialPrizes: string[];
  consolationPrizes: string[];
  fiveDFirstPrize?: string;
  fiveDSecondPrize?: string;
  fiveDThirdPrize?: string;
  sixDPrize?: string;
}

interface Operator {
  id: number;
  name: string;
  code: string;
}

const LotteryResults: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  // Fetch lottery results
  const { data: groupedResults = [], isLoading: resultsLoading, error } = useQuery<OperatorLotteryResults[]>({
    queryKey: ['lottery-results-grouped', selectedDate],
    queryFn: async () => {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const response = await fetch(`${API_BASE_URL}/api/lottery-results/grouped?drawDate=${selectedDate}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch lottery results');
      return response.json();
    },
    enabled: !!selectedDate
  });

  const getOperatorColor = (operatorName: string) => {
    const colors: { [key: string]: string } = {
      'Magnum 4D': '#e91e63',
      'Damacai 4D': '#9c27b0',
      'Singapore 4D': '#673ab7',
      'Sports Toto': '#3f51b5',
      'Cash Sweep': '#2196f3',
      'Sabah 88': '#03a9f4',
      'Grand Dragon': '#009688',
      '9 Lotto': '#4caf50'
    };
    return colors[operatorName] || '#757575';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const dayName = days[date.getDay()];
    return `${date.toISOString().split('T')[0]}[${dayName}]`;
  };

  const renderNumberGrid = (numbers: string[], columns: number = 5) => {
    const rows = [];
    for (let i = 0; i < numbers.length; i += columns) {
      rows.push(numbers.slice(i, i + columns));
    }
    return rows;
  };

  if (resultsLoading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: '#f8fafc',
      padding: 3
    }}>
      {/* Modern Header */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: 3,
        padding: 4,
        mb: 4,
        color: 'white',
        textAlign: 'center'
      }}>
        <Typography variant="h3" sx={{ fontWeight: 800, mb: 1 }}>
          🎰 Lottery Results
        </Typography>
        <Typography variant="h6" sx={{ opacity: 0.9, mb: 3 }}>
          Check your winning numbers
        </Typography>
        <Box sx={{ 
          display: 'inline-flex', 
          backgroundColor: 'rgba(255,255,255,0.2)',
          borderRadius: 2,
          padding: 1,
          backdropFilter: 'blur(10px)'
        }}>
          <TextField
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white',
                '& fieldset': { border: 'none' }
              }
            }}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          Failed to load lottery results. Please try again later.
        </Alert>
      )}

      {/* Results Grid */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          lg: 'repeat(3, 1fr)'
        },
        gap: 4
      }}>
        {groupedResults.map((operatorResult) => {
          const result = operatorResult.results[0];
          if (!result) return null;

          return (
            <Card key={operatorResult.operatorId} sx={{ 
              borderRadius: 3,
              overflow: 'hidden',
              boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 20px 40px rgba(0,0,0,0.15)'
              }
            }}>
              {/* Card Header */}
              <Box sx={{
                background: `linear-gradient(135deg, ${getOperatorColor(operatorResult.operatorName)} 0%, ${getOperatorColor(operatorResult.operatorName)}CC 100%)`,
                padding: 3,
                color: 'white',
                textAlign: 'center'
              }}>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                  {operatorResult.operatorName}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {formatDate(operatorResult.drawDate)}
                </Typography>
              </Box>

              <CardContent sx={{ p: 4 }}>
                {/* Main Prizes Section */}
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" sx={{ 
                    fontWeight: 600, 
                    mb: 3, 
                    color: '#374151',
                    textAlign: 'center'
                  }}>
                    🏆 Main Prizes
                  </Typography>
                  
                  {/* 1st Prize */}
                  <Box sx={{
                    backgroundColor: '#fef3c7',
                    borderRadius: 2,
                    padding: 2,
                    mb: 2,
                    textAlign: 'center',
                    border: '2px solid #f59e0b'
                  }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#92400e', mb: 1 }}>
                      1ST PRIZE
                    </Typography>
                    <Typography variant="h4" sx={{ 
                      fontWeight: 800, 
                      color: '#92400e',
                      letterSpacing: '0.1em'
                    }}>
                      {result.firstPrize}
                    </Typography>
                  </Box>

                  {/* 2nd & 3rd Prizes */}
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{
                      backgroundColor: '#fecaca',
                      borderRadius: 2,
                      padding: 2,
                      flex: 1,
                      textAlign: 'center',
                      border: '2px solid #ef4444'
                    }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#991b1b', mb: 1 }}>
                        2ND PRIZE
                      </Typography>
                      <Typography variant="h5" sx={{ 
                        fontWeight: 700, 
                        color: '#991b1b',
                        letterSpacing: '0.1em'
                      }}>
                        {result.secondPrize}
                      </Typography>
                    </Box>
                    
                    <Box sx={{
                      backgroundColor: '#bbf7d0',
                      borderRadius: 2,
                      padding: 2,
                      flex: 1,
                      textAlign: 'center',
                      border: '2px solid #22c55e'
                    }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#166534', mb: 1 }}>
                        3RD PRIZE
                      </Typography>
                      <Typography variant="h5" sx={{ 
                        fontWeight: 700, 
                        color: '#166534',
                        letterSpacing: '0.1em'
                      }}>
                        {result.thirdPrize}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Special Numbers */}
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" sx={{ 
                    fontWeight: 600, 
                    mb: 2, 
                    color: '#374151',
                    textAlign: 'center'
                  }}>
                    ⭐ Special ({result.specialPrizes.length} numbers)
                  </Typography>
                  <Box sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    justifyContent: 'center'
                  }}>
                    {result.specialPrizes.map((number, index) => (
                      <Box
                        key={index}
                        sx={{
                          backgroundColor: '#fef3c7',
                          border: '2px solid #f59e0b',
                          borderRadius: 1,
                          padding: '8px 12px',
                          minWidth: '60px',
                          textAlign: 'center'
                        }}
                      >
                        <Typography variant="body2" sx={{ 
                          fontWeight: 700, 
                          color: '#92400e',
                          letterSpacing: '0.05em'
                        }}>
                          {number}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>

                {/* Consolation Numbers */}
                <Box sx={{ mb: result.fiveDFirstPrize || result.sixDPrize ? 4 : 0 }}>
                  <Typography variant="h6" sx={{ 
                    fontWeight: 600, 
                    mb: 2, 
                    color: '#374151',
                    textAlign: 'center'
                  }}>
                    🎁 Consolation ({result.consolationPrizes.length} numbers)
                  </Typography>
                  <Box sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    justifyContent: 'center'
                  }}>
                    {result.consolationPrizes.map((number, index) => (
                      <Box
                        key={index}
                        sx={{
                          backgroundColor: '#f3e8ff',
                          border: '2px solid #a855f7',
                          borderRadius: 1,
                          padding: '8px 12px',
                          minWidth: '60px',
                          textAlign: 'center'
                        }}
                      >
                        <Typography variant="body2" sx={{ 
                          fontWeight: 700, 
                          color: '#7c3aed',
                          letterSpacing: '0.05em'
                        }}>
                          {number}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>

                {/* 5D Results - Only for operators that support it */}
                {(result.fiveDFirstPrize || result.fiveDSecondPrize || result.fiveDThirdPrize) && (
                  <Box sx={{ mb: result.sixDPrize ? 4 : 0 }}>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 600, 
                      mb: 3, 
                      color: '#374151',
                      textAlign: 'center'
                    }}>
                      🎲 5D Results
                    </Typography>
                    
                    <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                      {result.fiveDFirstPrize && (
                        <Box sx={{
                          backgroundColor: '#e0f7fa',
                          borderRadius: 2,
                          padding: 2,
                          flex: 1,
                          textAlign: 'center',
                          border: '2px solid #00bcd4'
                        }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#006064', mb: 1 }}>
                            5D 1ST PRIZE
                          </Typography>
                          <Typography variant="h5" sx={{ 
                            fontWeight: 700, 
                            color: '#006064',
                            letterSpacing: '0.1em'
                          }}>
                            {result.fiveDFirstPrize}
                          </Typography>
                        </Box>
                      )}
                      
                      {result.fiveDSecondPrize && (
                        <Box sx={{
                          backgroundColor: '#e0f7fa',
                          borderRadius: 2,
                          padding: 2,
                          flex: 1,
                          textAlign: 'center',
                          border: '2px solid #00bcd4'
                        }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#006064', mb: 1 }}>
                            5D 2ND PRIZE
                          </Typography>
                          <Typography variant="h5" sx={{ 
                            fontWeight: 700, 
                            color: '#006064',
                            letterSpacing: '0.1em'
                          }}>
                            {result.fiveDSecondPrize}
                          </Typography>
                        </Box>
                      )}
                      
                      {result.fiveDThirdPrize && (
                        <Box sx={{
                          backgroundColor: '#e0f7fa',
                          borderRadius: 2,
                          padding: 2,
                          flex: 1,
                          textAlign: 'center',
                          border: '2px solid #00bcd4'
                        }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#006064', mb: 1 }}>
                            5D 3RD PRIZE
                          </Typography>
                          <Typography variant="h5" sx={{ 
                            fontWeight: 700, 
                            color: '#006064',
                            letterSpacing: '0.1em'
                          }}>
                            {result.fiveDThirdPrize}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}

                {/* 6D Result - Only for operators that support it */}
                {result.sixDPrize && (
                  <Box>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 600, 
                      mb: 3, 
                      color: '#374151',
                      textAlign: 'center'
                    }}>
                      💎 6D Result
                    </Typography>
                    
                    <Box sx={{
                      backgroundColor: '#f3e5f5',
                      borderRadius: 2,
                      padding: 3,
                      textAlign: 'center',
                      border: '2px solid #9c27b0',
                      maxWidth: '300px',
                      margin: '0 auto'
                    }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#6a1b9a', mb: 1 }}>
                        6D PRIZE
                      </Typography>
                      <Typography variant="h4" sx={{ 
                        fontWeight: 800, 
                        color: '#6a1b9a',
                        letterSpacing: '0.1em'
                      }}>
                        {result.sixDPrize}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          );
        })}
      </Box>

      {groupedResults.length === 0 && !resultsLoading && (
        <Card sx={{ 
          p: 6, 
          textAlign: 'center', 
          mt: 4,
          borderRadius: 3,
          backgroundColor: '#f8fafc',
          border: '2px dashed #d1d5db'
        }}>
          <Typography variant="h5" sx={{ mb: 2, color: '#6b7280' }}>
            📅 No Results Found
          </Typography>
          <Typography variant="body1" color="text.secondary">
            No lottery results available for the selected date.
            <br />
            Try selecting a different date or check back later.
          </Typography>
        </Card>
      )}
    </Box>
  );
};

export default LotteryResults;